import PropTypes from 'prop-types';
import { Box, TextField, Button, Chip, Paper } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Iconify from '../iconify';

export default function SearchSection({
  searchQuery,
  onSearchQueryChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  onSearch,
  recentSearches,
  onRecentSearchClick,
  searchInputRef,
  targetName,
  onTargetNameChange,
  sx = {},
}) {
  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: 1, 
        mb: 3, 
        borderRadius: '8px',
        backgroundColor: 'background.paper',
        ...sx
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        flexWrap: 'nowrap',
        gap: 1 
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          flex: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          px: 1,
        }}>
          <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20, mr: 1 }} />
          <TextField
            fullWidth
            placeholder="Search..."
            value={searchQuery}
            onChange={onSearchQueryChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onSearch();
              }
            }}
            inputRef={searchInputRef}
            variant="standard"
            InputProps={{
              disableUnderline: true,
            }}
            sx={{ '& .MuiInputBase-root': { py: 0.5 } }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={onStartDateChange}
              slotProps={{ 
                textField: { 
                  size: "small",
                  variant: "outlined",
                  sx: { width: '100%' }
                } 
              }}
            />
          </LocalizationProvider>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={onEndDateChange}
              slotProps={{ 
                textField: { 
                  size: "small",
                  variant: "outlined",
                  sx: { width: '100%' }
                } 
              }}
            />
          </LocalizationProvider>
        </Box>
        
        <Button
          variant="contained"
          onClick={onSearch}
          disabled={!searchQuery}
          sx={{ 
            px: 3,
            py: 1,
            borderRadius: 1,
            whiteSpace: 'nowrap',
            boxShadow: 2 
          }}
        >
          Search
        </Button>
      </Box>
      {recentSearches?.length > 0 && (
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1, 
          mt: 1.5,
          mx: 1,
          justifyContent: 'center' // Centering the chips
        }}>
          {recentSearches.map((search, index) => {
            const searchText = typeof search === 'string' ? search : search.query;
            const targetName = typeof search === 'string' ? null : search.targetName;
            return (
              <Chip
                key={index}
                label={targetName ? `${searchText} (${targetName})` : searchText}
                onClick={() => onRecentSearchClick(search)}
                size="small"
                color="primary"
                variant="outlined"
                sx={{
                  borderRadius: 1,
                  backgroundColor: 'background.neutral',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              />
            );
          })}
        </Box>
      )}
    </Paper>
  );
}

SearchSection.propTypes = {
  searchQuery: PropTypes.string.isRequired,
  onSearchQueryChange: PropTypes.func.isRequired,
  startDate: PropTypes.instanceOf(Date),
  onStartDateChange: PropTypes.func.isRequired,
  endDate: PropTypes.instanceOf(Date),
  onEndDateChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  recentSearches: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        query: PropTypes.string.isRequired,
        targetName: PropTypes.string
      })
    ])
  ),
  onRecentSearchClick: PropTypes.func.isRequired,
  searchInputRef: PropTypes.object,
  targetName: PropTypes.string,
  onTargetNameChange: PropTypes.func,
  sx: PropTypes.object,
}; 